// src/pages/Signup.js — signup, email first.
//
// email -> 6-digit code sent to that email -> username -> password. Email works
// in every country, where SMS codes did not. A phone number is only needed to
// create invitations, and is verified from Account Details at that point.
//
// Codes are sent, checked and spent by the auth-codes Edge Function, shared
// with the app, which rate-limits sends and caps attempts on the server.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiMail, FiLock, FiUser, FiCheck, FiX } from 'react-icons/fi';
import supabase from '../services/supabase';
import {
  sendSignupEmailCode,
  verifySignupEmailCode,
  completeEmailSignup,
} from '../services/authCodes';
import '../styles/Auth.scss';

const RESEND_COOLDOWN = 30;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const STEPS = ['email', 'otp', 'username', 'password'];

const Signup = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('email');
  const [email, setEmail] = useState('');
  const [signupToken, setSignupToken] = useState(null);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [username, setUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef(null);

  const normalisedEmail = email.trim().toLowerCase();
  const emailValid = EMAIL_RE.test(normalisedEmail);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startResendTimer = () => {
    setResendTimer(RESEND_COOLDOWN);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Username availability, debounced.
  useEffect(() => {
    if (username.length < 3) {
      setUsernameAvailable(null);
      return undefined;
    }
    if (!/^[a-z0-9_]{3,20}$/.test(username)) {
      setError('Username must be 3-20 characters (letters, numbers, underscore only)');
      setUsernameAvailable(false);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      setCheckingUsername(true);
      setError(null);
      try {
        const { data } = await supabase
          .from('usernames')
          .select('username')
          .eq('username', username)
          .maybeSingle();
        setUsernameAvailable(!data);
        if (data) setError('Username is already taken');
      } catch (e) {
        console.error('Error checking username:', e);
      } finally {
        setCheckingUsername(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [username]);

  const handleSendCode = async (e) => {
    e?.preventDefault();
    if (!emailValid) {
      setError('Please enter a valid email address');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await sendSignupEmailCode(normalisedEmail);
      startResendTimer();
      setOtp(['', '', '', '', '', '']);
      setCurrentStep('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      setSignupToken(await verifySignupEmailCode(normalisedEmail, code));
      setCurrentStep('username');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameSubmit = (e) => {
    e.preventDefault();
    if (!usernameAvailable || username.length < 3) {
      setError('Please choose an available username');
      return;
    }
    setError(null);
    setCurrentStep('password');
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      await completeEmailSignup({ email: normalisedEmail, signupToken, username, password });

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: normalisedEmail,
        password,
      });
      if (signInError) {
        setError('Account created, but signing in failed. Please sign in with your email.');
        return;
      }
      navigate('/profile-setup');
    } catch (err) {
      // Send them to the step that fixes it.
      if (err.code === 'token_expired') {
        setCurrentStep('email');
      } else if (err.code === 'username_taken') {
        setUsernameAvailable(false);
        setCurrentStep('username');
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // A pasted code fills every box at once.
    const digits = value.replace(/\D/g, '');
    if (digits.length > 1) {
      const next = [...otp];
      digits.slice(0, 6 - index).split('').forEach((d, i) => { next[index + i] = d; });
      setOtp(next);
      document.getElementById(`otp-${Math.min(index + digits.length, 5)}`)?.focus();
      return;
    }
    const next = [...otp];
    next[index] = digits;
    setOtp(next);
    if (digits && index < 5) document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleBack = () => {
    setError(null);
    const i = STEPS.indexOf(currentStep);
    if (i <= 0) navigate('/');
    else setCurrentStep(STEPS[i - 1]);
  };

  const stepIndex = STEPS.indexOf(currentStep);

  return (
    <div className="auth-container">
      <div className="auth-background">
        <img src="/images/events/3.jpg" alt="Background" />
        <div className="auth-overlay"></div>
      </div>

      <div className="auth-content">
        <button onClick={handleBack} className="back-button" aria-label="Back">
          <FiArrowLeft />
        </button>

        <motion.div
          className="auth-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="auth-title">Create Account</h1>
          <p className="auth-subtitle">
            {currentStep === 'email' && 'Enter your email to get started'}
            {currentStep === 'otp' && 'Enter the code we emailed you'}
            {currentStep === 'username' && 'Choose a unique username'}
            {currentStep === 'password' && 'Create a secure password'}
          </p>

          <div className="step-indicator">
            {STEPS.map((s, i) => (
              <div key={s} className={`step ${i <= stepIndex ? 'active' : ''}`}></div>
            ))}
          </div>

          {currentStep === 'email' && (
            <form onSubmit={handleSendCode}>
              <div className="input-group">
                <label htmlFor="signup-email">Email</label>
                <div className="input-with-icon">
                  <FiMail className="input-icon" />
                  <input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    autoFocus
                  />
                </div>
              </div>

              {error && (
                <div>
                  <p className="error-text">{error}</p>
                  {/already exists/i.test(error) && (
                    <div style={{ marginTop: '8px', textAlign: 'center' }}>
                      <Link to="/login" className="link-primary">Go to Sign In →</Link>
                    </div>
                  )}
                </div>
              )}

              <button type="submit" className="btn btn-primary" disabled={loading || !emailValid}>
                {loading ? 'Sending code...' : 'Continue'}
              </button>
            </form>
          )}

          {currentStep === 'otp' && (
            <form onSubmit={handleVerifyCode}>
              <div className="otp-info">
                <p>Code sent to {normalisedEmail}. Check your spam folder if it isn't there in a minute.</p>
              </div>

              <div className="otp-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="otp-input"
                    autoFocus={index === 0}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>

              {error && <p className="error-text text-center">{error}</p>}

              <button type="submit" className="btn btn-primary" disabled={loading || otp.join('').length !== 6}>
                {loading ? 'Verifying...' : 'Verify'}
              </button>

              <div className="resend-container">
                {resendTimer > 0 ? (
                  <p className="resend-text">Resend code in {resendTimer}s</p>
                ) : (
                  <>
                    <span className="resend-text">Didn't receive the code? </span>
                    <button type="button" className="btn-link" onClick={handleSendCode} disabled={loading}>
                      Resend
                    </button>
                  </>
                )}
              </div>
            </form>
          )}

          {currentStep === 'username' && (
            <form onSubmit={handleUsernameSubmit}>
              <div className="input-group">
                <label htmlFor="signup-username">Username</label>
                <div className="input-with-icon">
                  <FiUser className="input-icon" />
                  <input
                    id="signup-username"
                    type="text"
                    placeholder="Choose username"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                      setError(null);
                    }}
                    maxLength={20}
                    autoFocus
                  />
                  {checkingUsername && <div className="spinner-small"></div>}
                  {!checkingUsername && usernameAvailable === true && <FiCheck className="input-status success" />}
                  {!checkingUsername && usernameAvailable === false && <FiX className="input-status error" />}
                </div>
                {usernameAvailable && !error && <span className="success-text">✓ Username is available</span>}
              </div>

              {error && <p className="error-text">{error}</p>}

              <button type="submit" className="btn btn-primary" disabled={loading || !usernameAvailable || username.length < 3}>
                Continue
              </button>
            </form>
          )}

          {currentStep === 'password' && (
            <form onSubmit={handleCreateAccount}>
              <div className="input-group">
                <label htmlFor="signup-password">Password</label>
                <div className="input-with-icon">
                  <FiLock className="input-icon" />
                  <input
                    id="signup-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null); }}
                    autoFocus
                  />
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="signup-confirm">Confirm Password</label>
                <div className="input-with-icon">
                  <FiLock className="input-icon" />
                  <input
                    id="signup-confirm"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
                  />
                </div>
              </div>

              {error && <p className="error-text">{error}</p>}

              <button type="submit" className="btn btn-primary" disabled={loading || !password || !confirmPassword}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          )}

          <div className="auth-footer">
            <p>Already have an account? <Link to="/login">Sign In</Link></p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Signup;
